import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import HelpIcon from '@mui/icons-material/Help';
import AddIcon from '@mui/icons-material/AddRounded';
import TranslateIcon from '@mui/icons-material/TranslateRounded';
import { Input } from '@components/Input';
import { Checkbox } from '@components/Checkbox';
import { Select } from '@components/Select';
import { MultiSelect } from '@components/MultiSelect';
import Button from '@components/Button';
import Tooltip from '@components/Tooltip';
import { Typography } from '@components/Typography';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { BuilderCard, BuilderCardHeader } from '@components/BuilderCard';
import { useTranslation } from '@hooks';
import { QuestionType, QUESTION_TYPE_OPTIONS } from '@constants/surveys';
import QuestionOptions from './QuestionOptions';
import { CheckboxRow } from './styles';

const Form = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'calc(50% - 8px) calc(50% - 8px)',
    gridGap: 16,
    margin: '8px 0',
    paddingTop: 8,
    '@media (max-width: 1200px)': {
        gridTemplateColumns: '100%',
    },
    '@media (max-width: 768px)': {
        gridTemplateColumns: '100%',
    },
});

const ConditionFields = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'calc(50% - 8px) calc(50% - 8px)',
    gridGap: 16,
    '@media (max-width: 1200px)': {
        gridTemplateColumns: '100%',
    },
    '@media (max-width: 768px)': {
        gridTemplateColumns: '100%',
    },
});

const ConditionFieldGroup = styled('div')({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    minWidth: 0,
});

const ConditionsWrapper = styled('div')({
    padding: '8px 0',
});

const getValueOptionsForQuestion = (sourceQuestion, t) => {
    if (!sourceQuestion) return [];
    const type = sourceQuestion.type || QuestionType.RATING;
    switch (type) {
        case QuestionType.RATING:
            return [1, 2, 3, 4, 5].map(v => ({
                value: String(v),
                label: `${v} ${v === 1 ? t('FeedbackSurveys.star') : t('FeedbackSurveys.stars')}`,
            }));
        case QuestionType.YES_NO:
            return [
                { value: '1', label: t('common.yes') },
                { value: '0', label: t('common.no') },
            ];
        case QuestionType.SINGLE_CHOICE:
        case QuestionType.MULTI_SELECT: {
            const options = (sourceQuestion.options || [])
                .filter(o => !o.hidden)
                .sort((a, b) => {
                    if (a.isOther !== b.isOther) return a.isOther ? 1 : -1;
                    return (a.position ?? 0) - (b.position ?? 0);
                });
            return options.map(o => ({ value: String(o.id), label: o.label || `Option ${o.id}` }));
        }
        default:
            return [];
    }
};

const QuestionItem = ({
    listeners,
    attributes,
    isView,
    index,
    question,
    isOpen,
    hasError,
    onClick,
    onChange,
    onChangeRequired,
    onChangeIsGate,
    onChangeConditions,
    onDelete,
    onOptionsChange,
    isTitleLocked,
    setLocalizeModal,
    isLocalizationDisabled,
    previousQuestions,
}) => {
    const { t } = useTranslation();

    const questionType = question?.type || QuestionType.RATING;
    const hasOptions = questionType === QuestionType.SINGLE_CHOICE || questionType === QuestionType.MULTI_SELECT;
    const typeLabel = QUESTION_TYPE_OPTIONS.find(opt => opt.value === questionType)?.label;

    return (
        <BuilderCard isExpanded={isOpen} hasError={hasError}>
            <BuilderCardHeader
                caption={typeLabel ? t(typeLabel) : questionType}
                title={question?.title}
                isExpanded={isOpen}
                hasError={hasError}
                onToggle={() => onClick(+index)}
                listeners={listeners}
                attributes={attributes}
            />
            {isOpen ? (
                <>
                    <Form>
                        <Input
                            inputProps={{
                                name: 'title',
                                value: question?.title,
                                onChange: e => onChange(e, index, question?.language),
                            }}
                            label={t('common.title')}
                            error={!question?.title}
                            helperText={!question?.title ? t('validation.required') : ''}
                            fullWidth
                            required
                            disabled={isView || isTitleLocked}
                        />
                        <Input
                            inputProps={{
                                name: 'question',
                                value: question?.question,
                                onChange: e => onChange(e, index, question?.language),
                            }}
                            label={t('common.question')}
                            error={!question?.question}
                            helperText={!question?.question ? t('validation.required') : ''}
                            fullWidth
                            disabled={isView}
                        />
                    </Form>
                    {hasOptions && (
                        <QuestionOptions
                            options={question?.options || []}
                            onChange={options => onOptionsChange(options, index)}
                            isView={isView}
                        />
                    )}
                    <CheckboxRow sx={{ marginTop: '8px' }}>
                        <Tooltip
                            title={question?.isGate ? t('FeedbackSurveys.gateRequiresTooltip') : ''}
                            placement="top"
                            enterTouchDelay={0}
                        >
                            <span>
                                <Checkbox
                                    disabled={isView || !!question?.isGate}
                                    inputProps={{ 'aria-label': 'controlled' }}
                                    onChange={e => onChangeRequired(index, e.target.checked)}
                                    checked={!!question?.required || !!question?.isGate}
                                />
                            </span>
                        </Tooltip>
                        <Typography variant="body2">{t('common.required')}</Typography>
                    </CheckboxRow>
                    {questionType === QuestionType.YES_NO && (
                        <CheckboxRow sx={{ marginTop: '4px' }}>
                            <Checkbox
                                disabled={isView}
                                inputProps={{ 'aria-label': 'controlled' }}
                                onChange={e => onChangeIsGate(index, e.target.checked)}
                                checked={!!question?.isGate}
                            />
                            <Typography variant="body2">{t('FeedbackSurveys.gateQuestion')}</Typography>
                            <IconButton sx={{ padding: '4px !important', marginLeft: '4px' }}>
                                <Tooltip
                                    title={t('FeedbackSurveys.gateQuestionTooltip')}
                                    placement="top"
                                    enterTouchDelay={0}
                                >
                                    <HelpIcon sx={{ height: '18px !important', width: '18px !important' }} />
                                </Tooltip>
                            </IconButton>
                        </CheckboxRow>
                    )}
                    {(previousQuestions?.length > 0 || question?.conditions?.length > 0) && (
                        <>
                            <CheckboxRow sx={{ marginTop: '4px' }}>
                                <Checkbox
                                    disabled={isView || !!question?.isGate}
                                    inputProps={{ 'aria-label': 'controlled' }}
                                    onChange={e => {
                                        if (e.target.checked) {
                                            onChangeConditions(index, [{ questionTitle: '', values: [] }]);
                                        } else {
                                            onChangeConditions(index, null);
                                        }
                                    }}
                                    checked={!!question?.conditions?.length}
                                />
                                <Typography variant="body2">{t('FeedbackSurveys.showConditionally')}</Typography>
                            </CheckboxRow>
                            {question?.conditions?.length > 0 && (
                                <ConditionsWrapper>
                                    {question.conditions.map((condition, condIndex) => {
                                        const sourceQuestion = previousQuestions.find(
                                            q => q.title === condition.questionTitle,
                                        );
                                        const valueOptions = getValueOptionsForQuestion(sourceQuestion, t);
                                        return (
                                            <div key={condIndex}>
                                                <Typography variant="body2" sx={{ margin: '8px 0' }}>
                                                    {condIndex === 0
                                                        ? t('FeedbackSurveys.conditionShowIf')
                                                        : t('FeedbackSurveys.conditionAndIf')}
                                                </Typography>
                                                <ConditionFields>
                                                    <ConditionFieldGroup>
                                                        <Select
                                                            label={t('common.question')}
                                                            value={condition.questionTitle || ''}
                                                            onChange={e => {
                                                                const newConditions = [...question.conditions];
                                                                newConditions[condIndex] = {
                                                                    questionTitle: e.target.value,
                                                                    values: [],
                                                                };
                                                                onChangeConditions(index, newConditions);
                                                            }}
                                                            options={previousQuestions
                                                                .filter(q => {
                                                                    const usedTitles = question.conditions
                                                                        .filter((_, i) => i !== condIndex)
                                                                        .map(c => c.questionTitle);
                                                                    return !usedTitles.includes(q.title);
                                                                })
                                                                .map(q => ({
                                                                    value: q.title,
                                                                    label: q.title,
                                                                }))}
                                                            disabled={isView}
                                                            error={!condition.questionTitle}
                                                            style={{ flex: 1, paddingBottom: 0 }}
                                                        />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                flexShrink: 0,
                                                                paddingTop: '16px',
                                                                marginBottom: '16px',
                                                            }}
                                                        >
                                                            {t('FeedbackSurveys.conditionMatches')}
                                                        </Typography>
                                                    </ConditionFieldGroup>
                                                    <ConditionFieldGroup>
                                                        <MultiSelect
                                                            label={t('common.answer')}
                                                            value={condition.values || []}
                                                            onChange={newValues => {
                                                                const newConditions = [...question.conditions];
                                                                newConditions[condIndex] = {
                                                                    ...newConditions[condIndex],
                                                                    values: newValues,
                                                                };
                                                                onChangeConditions(index, newConditions);
                                                            }}
                                                            options={valueOptions}
                                                            disabled={isView || !condition.questionTitle}
                                                            error={
                                                                condition.questionTitle && !condition.values?.length
                                                                    ? { message: 'validation.required' }
                                                                    : null
                                                            }
                                                            style={{ flex: 1 }}
                                                        />
                                                        {condIndex > 0 && (
                                                            <IconButton
                                                                onClick={() => {
                                                                    const newConditions = question.conditions.filter(
                                                                        (_, i) => i !== condIndex,
                                                                    );
                                                                    onChangeConditions(index, newConditions);
                                                                }}
                                                                disabled={isView}
                                                                sx={{
                                                                    flexShrink: 0,
                                                                    marginTop: '3px',
                                                                    padding: '4px',
                                                                    width: 32,
                                                                    height: 32,
                                                                    '& svg path': { fill: 'var(--text-secondary)' },
                                                                }}
                                                            >
                                                                <DeleteIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        )}
                                                    </ConditionFieldGroup>
                                                </ConditionFields>
                                            </div>
                                        );
                                    })}
                                    <Button
                                        variant="text"
                                        size="small"
                                        onClick={() => {
                                            onChangeConditions(index, [
                                                ...question.conditions,
                                                { questionTitle: '', values: [] },
                                            ]);
                                        }}
                                        disabled={isView}
                                        startIcon={<AddIcon />}
                                    >
                                        {t('FeedbackSurveys.addCondition')}
                                    </Button>
                                </ConditionsWrapper>
                            )}
                        </>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            marginTop: 8,
                        }}
                    >
                        <Tooltip
                            title={isLocalizationDisabled ? t('FeedbackSurveys.localizationSingleLanguage') : ''}
                            placement="top"
                            enterTouchDelay={0}
                        >
                            <span>
                                <Button
                                    variant="outlined"
                                    startIcon={<TranslateIcon />}
                                    onClick={() => setLocalizeModal({ open: true, index })}
                                    disabled={isView || isLocalizationDisabled}
                                >
                                    {t('buttons.localization')}
                                </Button>
                            </span>
                        </Tooltip>
                        <div>
                            <Button variant="danger" size="large" onClick={() => onDelete(index)} disabled={isView}>
                                {t('buttons.delete')}
                            </Button>
                        </div>
                    </div>
                </>
            ) : null}
        </BuilderCard>
    );
};

export default QuestionItem;
